'use client'

import useLocalStorageState from '@components/hooks/useLocalStorageState'
import {
  PhoneNumberInput,
  type PhoneNumberInputCustomRef,
} from '@components/primitives/Form/Inputs/PhoneNumberInput'
import { Text } from '@components/primitives/Text'
import { simpleE164Formatter } from '@lib/utilities/phoneNumber/simpleE164Formatter'
import {
  type AutoCompleteValues,
  autoCompleteLocalStorageKey,
} from '@modules/patientTrialApplications/autocomplete'
import type { E164Number } from 'libphonenumber-js'
import { useEffect, useRef, useState } from 'react'
import { debounceTrackQuestionModified } from './trackQuestionModified'
import type { DemographicsCollectElementProps } from './types'

const debouncedEventTracking = debounceTrackQuestionModified('phoneNumber')

export default function CollectPhoneNumber({
  formRef,
  patientProfileData: { phoneNumber },
  questionConfig,
}: DemographicsCollectElementProps) {
  const customRef = useRef<PhoneNumberInputCustomRef>(null)

  const [e164Number, setE164Number] = useState<E164Number | undefined>(
    phoneNumber[0],
  )

  const [autoCompleteValues] = useLocalStorageState<AutoCompleteValues>(
    autoCompleteLocalStorageKey,
    {},
  )

  const handleInputChange = (newInputValue: E164Number) => {
    setE164Number(newInputValue)
    debouncedEventTracking(newInputValue)
    // trigger an "input" event on the form ref so that the form event listeners can update validity
    setTimeout(() => formRef?.current?.dispatchEvent(new Event('input')), 50)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Return') {
      e.preventDefault()
      e.currentTarget.form?.requestSubmit()
    }
  }

  useEffect(() => {
    if (autoCompleteValues.phone && !e164Number) {
      handleInputChange(simpleE164Formatter(autoCompleteValues.phone))
    }

    const timeout = setTimeout(() => customRef.current?.focusInput(), 500)

    return () => clearTimeout(timeout)
  }, [e164Number, autoCompleteValues.phone])

  return (
    <div className='flex w-full flex-col gap-2'>
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='h3'
        value='What phone number can the recruitment coordinator reach you?'
      />
      <Text
        className='text-neutral900'
        element={'p'}
        styleName='p-small'
        value='Enter your phone number'
      />
      <PhoneNumberInput
        name={questionConfig.id}
        onKeyDown={handleKeyDown}
        ref={customRef}
        required={!questionConfig.skippable}
        setValue={handleInputChange}
        value={e164Number}
      />
    </div>
  )
}
